import React, { useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import { parseSplitAmount } from "../../utils/financeSplits";

const MOBILE_TEXT_MAX = 25;

function formatBookingDate(value, compact) {
  const raw = String(value || "");
  if (!compact) return raw;
  const [year, month, day] = raw.split("-");
  if (!year || !month || !day) return raw;
  return `${day}.${month}`;
}

function displayText(value, compact) {
  const text = value || "–";
  if (!compact || text.length <= MOBILE_TEXT_MAX) return text;
  return `${text.slice(0, MOBILE_TEXT_MAX)}...`;
}

function uniqueAccountCount(rows) {
  const ids = new Set();
  (rows || []).forEach((row) => {
    const id = row.account_uuid || row.account_name;
    if (id) ids.add(String(id));
  });
  return ids.size;
}

function flattenCategories(tree, prefix = "") {
  const out = [];
  (tree || []).forEach((node) => {
    const label = prefix ? `${prefix} / ${node.name}` : node.name;
    out.push({ category_uuid: node.category_uuid, label });
    (node.children || []).forEach((child) => {
      out.push({
        category_uuid: child.category_uuid,
        label: `${label} / ${child.name}`,
      });
    });
  });
  return out;
}

function categoryLabel(row) {
  const name = row.category_name || row.current_category_name;
  if (row.parent_category_name && name) {
    return `${row.parent_category_name} / ${name}`;
  }
  return name || "Ukategoriseret";
}

function isInternalTransferRow(row) {
  if (row.is_internal_transfer) return true;
  const name = row.category_name || row.current_category_name || "";
  return name === "Intern overførsel";
}

function isPendingRow(row, parent = null) {
  if (row?.is_pending) return true;
  return Boolean(parent?.is_pending);
}

function matchLabel(count) {
  if (!count) return "Ingen regel";
  if (count === 1) return "1 regel";
  return `${count} regler`;
}

function matchRowSx(count) {
  if (!count) {
    return { bgcolor: "rgba(180, 35, 24, 0.14)" };
  }
  if (count === 1) {
    return { bgcolor: "rgba(46, 125, 79, 0.16)" };
  }
  return { bgcolor: "rgba(196, 92, 38, 0.22)" };
}

function rowCategoryValue(row) {
  return row.category_overridden ? row.category_uuid || "" : "";
}

export default function FinanceTransactionList({
  rows = [],
  groups = null,
  formatMoney,
  emptyText = "Ingen posteringer.",
  colorByMatchCount = false,
  showMatchCount = false,
  stickyHeader = false,
  maxHeight,
  categories = [],
  onCategoryChange,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [editing, setEditing] = useState(null);
  const [draftCategory, setDraftCategory] = useState("");
  const [draftExempt, setDraftExempt] = useState(false);
  const [draftSplits, setDraftSplits] = useState([]);
  const [splitError, setSplitError] = useState("");
  const [saving, setSaving] = useState(false);
  const flat = groups ? groups.flatMap((group) => group.rows) : rows;
  const showAccountColumn = !isMobile && uniqueAccountCount(flat) > 1;
  const showCategoryColumn = !isMobile;
  const showRulesColumn = !isMobile && showMatchCount;
  const colCount = 3 + (showAccountColumn ? 1 : 0) + (showCategoryColumn ? 1 : 0) + (showRulesColumn ? 1 : 0);
  const canEditCategory = typeof onCategoryChange === "function";
  const flatCategories = useMemo(() => flattenCategories(categories), [categories]);
  const cellSx = isMobile ? { px: 0.75, py: 0.75, whiteSpace: "nowrap" } : undefined;
  const dateCellSx = isMobile ? { ...cellSx, width: 48 } : undefined;

  const openEditor = (row) => {
    if (!canEditCategory) return;
    setDraftCategory(rowCategoryValue(row));
    setDraftExempt(Boolean(row.exempt_from_budget));
    setDraftSplits(
      (row.splits || []).map((split, index) => ({
        key: split.split_uuid || String(index),
        split_uuid: split.split_uuid,
        label: split.label || "",
        amount: String(Math.abs(Number(split.amount || 0))).replace(".", ","),
        category_uuid: split.category_uuid || "",
      }))
    );
    setSplitError("");
    setEditing(row);
  };

  const originalAmount = Number(editing?.original_amount ?? editing?.amount ?? 0);
  const draftTaken = draftSplits.reduce(
    (sum, row) => sum + Math.abs(parseSplitAmount(row.amount)),
    0
  );
  const draftRemainder =
    originalAmount >= 0 ? originalAmount - draftTaken : originalAmount + draftTaken;

  const closeEditor = () => {
    if (saving) return;
    setEditing(null);
  };

  const saveCategory = async () => {
    if (!editing) return;
    const prepared = [];
    for (const [index, row] of draftSplits.entries()) {
      const label = String(row.label || "").trim();
      const raw = String(row.amount || "").trim();
      if (!label && !raw) continue;
      const amount = parseSplitAmount(row.amount);
      if (amount <= 0) {
        setSplitError("Hver split-linje skal have et beløb større end 0");
        return;
      }
      prepared.push({
        split_uuid: row.split_uuid || undefined,
        label: label || null,
        amount,
        category_uuid: row.category_uuid || null,
        sort_order: index,
        source: "manual",
      });
    }
    if (draftTaken > Math.abs(originalAmount) + 0.001) {
      setSplitError("Split-linjer overstiger posteringens beløb");
      return;
    }
    setSplitError("");
    setSaving(true);
    try {
      await onCategoryChange(
        editing.transaction_uuid,
        draftCategory || null,
        draftExempt,
        prepared
      );
      setEditing(null);
    } finally {
      setSaving(false);
    }
  };

  const updateSplit = (key, patch) => {
    setDraftSplits((rows) => rows.map((row) => (row.key === key ? { ...row, ...patch } : row)));
    setSplitError("");
  };

  const renderRow = (row, { isSplit = false, parent = null } = {}) => {
    const count = row.matching_rule_count ?? 0;
    const ruleHint = row.extra || row.matched_rule_label || matchLabel(count);
    const target = isSplit ? parent : row;
    const hasSplits = !isSplit && (row.splits || []).length > 0;
    const pending = isPendingRow(row, parent);
    const fullText = row.match_text || row.counterparty_name || row.remittance || "–";
    const fullCategory = categoryLabel(row);
    const shownLabel = displayText(isMobile ? fullCategory : fullText, isMobile);
    const internalSx = isInternalTransferRow(row)
      ? { color: "text.secondary" }
      : undefined;
    const pendingSx = pending ? { fontStyle: "italic" } : undefined;
    const mobileLabel = (
      <Box
        component="span"
        sx={isMobile ? { ...internalSx, ...pendingSx } : pendingSx}
      >
        {shownLabel}
      </Box>
    );
    return (
      <TableRow
        key={row.key || row.transaction_uuid}
        hover={canEditCategory}
        onClick={canEditCategory && target ? () => openEditor(target) : undefined}
        sx={{
          ...(isSplit
            ? {
                bgcolor: "action.hover",
                boxShadow: "inset 3px 0 0 0 rgba(0,0,0,0.16)",
              }
            : colorByMatchCount
              ? matchRowSx(count)
              : {}),
          ...(pending ? { fontStyle: "italic" } : {}),
          cursor: canEditCategory ? "pointer" : "default",
        }}
      >
        <TableCell sx={dateCellSx}>{isSplit ? "" : formatBookingDate(row.booking_date, isMobile)}</TableCell>
        {showAccountColumn ? <TableCell>{isSplit ? "" : row.account_name || "–"}</TableCell> : null}
        <TableCell sx={cellSx}>
          <Box sx={{ pl: isSplit ? 3 : 0, ...pendingSx }}>
            {isMobile && shownLabel !== fullCategory ? (
              <Tooltip title={fullCategory} placement="top">
                {mobileLabel}
              </Tooltip>
            ) : (
              mobileLabel
            )}
          </Box>
        </TableCell>
        <TableCell align="right" sx={cellSx}>
          <Box sx={pendingSx}>
            {formatMoney(row.amount, row.currency)}
            {hasSplits && row.original_amount != null ? (
              <Typography variant="caption" color="text.secondary" display="block">
                af {formatMoney(row.original_amount, row.currency)}
              </Typography>
            ) : null}
          </Box>
        </TableCell>
        {showCategoryColumn ? (
          <TableCell>
            <Tooltip title={ruleHint} placement="left">
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
                <Box component="span" sx={{ ...internalSx, ...pendingSx }}>
                  {categoryLabel(row)}
                </Box>
                {!isSplit && row.exempt_from_budget ? (
                  <Chip label="Undtaget" size="small" variant="outlined" />
                ) : null}
              </Box>
            </Tooltip>
          </TableCell>
        ) : null}
        {showRulesColumn ? (
          <TableCell>
            {isSplit ? null : (
              <Tooltip title={ruleHint} placement="left">
                <span>{matchLabel(count)}</span>
              </Tooltip>
            )}
          </TableCell>
        ) : null}
      </TableRow>
    );
  };

  const renderParentWithSplits = (row) => (
    <React.Fragment key={row.transaction_uuid}>
      {renderRow(row)}
      {(row.splits || []).map((split) =>
        renderRow(
          {
            key: split.split_uuid,
            booking_date: "",
            account_name: "",
            match_text: split.label || "Delpostering",
            amount: split.amount,
            currency: row.currency,
            category_name: split.category_name,
            parent_category_name: split.parent_category_name,
            category_uuid: split.category_uuid,
            matching_rule_count: 0,
          },
          { isSplit: true, parent: row }
        )
      )}
    </React.Fragment>
  );

  const table = (
    <Table
      size="small"
      stickyHeader={stickyHeader}
      sx={isMobile ? { tableLayout: "fixed", width: "100%" } : undefined}
    >
      <TableHead>
        <TableRow>
          <TableCell sx={dateCellSx}>Dato</TableCell>
          {showAccountColumn ? <TableCell>Konto</TableCell> : null}
          <TableCell sx={cellSx}>{isMobile ? "Kategori" : "Tekst"}</TableCell>
          <TableCell align="right" sx={cellSx}>Beløb</TableCell>
          {showCategoryColumn ? <TableCell>Kategori</TableCell> : null}
          {showRulesColumn ? <TableCell>Regler</TableCell> : null}
        </TableRow>
      </TableHead>
      <TableBody>
        {flat.length === 0 ? (
          <TableRow>
            <TableCell colSpan={colCount}>
              <Typography variant="body2" color="text.secondary">
                {emptyText}
              </Typography>
            </TableCell>
          </TableRow>
        ) : groups ? (
          groups.map((group) => (
            <React.Fragment key={group.key}>
              <TableRow>
                <TableCell
                  colSpan={colCount}
                  sx={{ bgcolor: "action.hover", fontWeight: 700, borderBottom: 0 }}
                >
                  {group.label}
                </TableCell>
              </TableRow>
              {group.rows.map(renderParentWithSplits)}
            </React.Fragment>
          ))
        ) : (
          rows.map(renderParentWithSplits)
        )}
      </TableBody>
    </Table>
  );

  const list = (
    <Box
      sx={{
        ...(maxHeight ? { maxHeight, overflow: "auto" } : {}),
        ...(isMobile ? { overflowX: "hidden", width: "100%" } : {}),
        ...(maxHeight
          ? { border: 1, borderColor: "divider", borderRadius: 1 }
          : {}),
      }}
    >
      {table}
    </Box>
  );

  return (
    <>
      {list}
      <Dialog open={Boolean(editing)} onClose={closeEditor} fullWidth maxWidth="sm">
        <DialogTitle>Kategoriser postering</DialogTitle>
        <DialogContent>
          {editing ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, pt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                {editing.booking_date}
                {editing.account_name ? ` · ${editing.account_name}` : ""}
              </Typography>
              <Typography sx={{ fontWeight: 600 }}>
                {editing.match_text || editing.counterparty_name || editing.remittance || "–"}
              </Typography>
              <Typography variant="body2">
                Originalt {formatMoney(originalAmount, editing.currency)}
                {" · "}
                Rest {formatMoney(draftRemainder, editing.currency)}
              </Typography>
              <FormControl size="small" fullWidth sx={{ mt: 1 }}>
                <InputLabel>Kategori</InputLabel>
                <Select
                  label="Kategori"
                  value={draftCategory}
                  onChange={(event) => setDraftCategory(event.target.value)}
                >
                  <MenuItem value="">
                    {editing.category_overridden ? "Automatisk (regex)" : "Automatisk"}
                  </MenuItem>
                  {flatCategories.map((cat) => (
                    <MenuItem key={cat.category_uuid} value={cat.category_uuid}>
                      {cat.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={draftExempt}
                    onChange={(event) => setDraftExempt(event.target.checked)}
                  />
                }
                label="Undtag fra budget"
              />
              <Typography variant="caption" color="text.secondary">
                Tæller ikke med i indtægt, udgift og budget når du kigger på denne konto.
                Brug det fx når du flytter penge fra opsparing for at betale en regning.
              </Typography>
              <Box sx={{ mt: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Split
                </Typography>
                {draftSplits.map((row) => (
                  <Box
                    key={row.key}
                    sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1 }}
                  >
                    <TextField
                      size="small"
                      label="Tekst"
                      value={row.label}
                      onChange={(event) => updateSplit(row.key, { label: event.target.value })}
                      sx={{ flex: 1.4 }}
                    />
                    <TextField
                      size="small"
                      label="Beløb"
                      value={row.amount}
                      onChange={(event) => updateSplit(row.key, { amount: event.target.value })}
                      sx={{ width: 110 }}
                    />
                    <FormControl size="small" sx={{ flex: 1.2 }}>
                      <InputLabel>Kategori</InputLabel>
                      <Select
                        label="Kategori"
                        value={row.category_uuid}
                        onChange={(event) =>
                          updateSplit(row.key, { category_uuid: event.target.value })
                        }
                      >
                        <MenuItem value="">Ukategoriseret</MenuItem>
                        {flatCategories.map((cat) => (
                          <MenuItem key={cat.category_uuid} value={cat.category_uuid}>
                            {cat.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    <IconButton
                      size="small"
                      aria-label="Slet linje"
                      onClick={() =>
                        setDraftSplits((rows) => rows.filter((item) => item.key !== row.key))
                      }
                    >
                      <DeleteOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
                <Button
                  size="small"
                  onClick={() =>
                    setDraftSplits((rows) => [
                      ...rows,
                      { key: `${Date.now()}`, label: "", amount: "", category_uuid: "" },
                    ])
                  }
                >
                  Tilføj linje
                </Button>
                {splitError ? (
                  <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
                    {splitError}
                  </Typography>
                ) : null}
              </Box>
            </Box>
          ) : null}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor} disabled={saving}>
            Annuller
          </Button>
          <Button variant="contained" onClick={saveCategory} disabled={saving}>
            {saving ? "Gemmer…" : "Gem"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
